"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { ShoppingCart, ChevronDown, Menu as MenuIcon, X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

const medicineCategories = [
  { label: "All Medicines",              path: "/products/all",                         icon: "💊" },
  { label: "Best Selling Medicines",     path: "/products/best-selling-medicines",      icon: "⭐" },
  { label: "Super Speciality Medicines", path: "/products/super-speciality-medicines", icon: "🧬" },
  { label: "Surgicals",                  path: "/products/surgicals",                  icon: "🩺" },
  { label: "Vaccines",                   path: "/products/vaccines",                   icon: "💉" },
]

const navLinks = [
  { label: "Home",    path: "/" },
  { label: "About",   path: "/about" },
  { label: "Contact", path: "/contact" },
]

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > threshold)
    window.addEventListener("scroll", fn, { passive: true })
    return () => window.removeEventListener("scroll", fn)
  }, [threshold])
  return scrolled
}

function useScrollDirection(threshold = 10) {
  const [direction, setDirection] = useState<'up' | 'down'>('up')
  const [scrollY, setScrollY] = useState(0)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const fn = () => {
      const y = window.scrollY
      setScrollY(y)
      if (Math.abs(y - lastScrollY.current) < threshold) return
      setDirection(y > lastScrollY.current ? 'down' : 'up')
      lastScrollY.current = y
    }
    window.addEventListener("scroll", fn, { passive: true })
    return () => window.removeEventListener("scroll", fn)
  }, [threshold])

  return { direction, scrollY }
}

export default function Header() {
  const location      = useLocation()
  const productsRef   = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrolled      = useScrolled()
  const [isMobileMenuOpen,     setIsMobileMenuOpen]     = useState(false)
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false)
  const [cartCount,            setCartCount]            = useState(0)
  const [showProductsDropdown, setShowProductsDropdown] = useState(false)
  const { direction: scrollDir, scrollY } = useScrollDirection()
  const shouldHide = scrollDir === 'down' && !isMobileMenuOpen && scrollY > 80

  useEffect(() => {
    updateCartCount()
    const handler = () => updateCartCount()
    window.addEventListener("cartUpdated", handler)
    return () => window.removeEventListener("cartUpdated", handler)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY
      document.documentElement.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
    } else {
      const top = document.body.style.top
      document.documentElement.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      window.scrollTo(0, parseInt(top || '0') * -1)
    }
    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (productsRef.current && !productsRef.current.contains(e.target as Node)) setShowProductsDropdown(false)
    }
    document.addEventListener("mousedown", fn)
    return () => document.removeEventListener("mousedown", fn)
  }, [])

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem("enquiryCart") || "[]")
    setCartCount(cart.length)
  }

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => setShowProductsDropdown(false), 120)
  }
  const cancelClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
  }

  const isProductsActive = location.pathname.startsWith("/products")

  const onTeal    = !scrolled
  const navTxt    = "#286074"
  const navActive = "#145369"
  const ulGrad    = "#145369"

  return (
    <>
      <header className={`sticky top-0 z-50 transition-transform duration-500 ${shouldHide ? '-translate-y-full' : 'translate-y-0'} lg:translate-y-0`}>

      {/* ── Header bar ── */}
      <div
        className="transition-all duration-500 relative"
        style={{
          background: "white",
          boxShadow:  "0 2px 16px rgba(0,0,0,0.08)",
        }}
      >
        {/* White shimmer bottom border — always visible */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${navTxt} 20%, ${navTxt} 50%, ${navTxt} 80%, transparent 100%)`,
          }}
        />

        <div className="max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 xs:gap-4 lg:gap-6 h-16 sm:h-20">

            {/* Logo */}
            <Link to="/" className="shrink-0 group">
              <img
                src="/images/Essess_Logo-cropped.svg"
                alt="eSSeSSpharma"
                width="200"
                height="64"
                fetchPriority="high"
                className="h-11 xs:h-14 sm:h-16 w-auto max-w-[130px] xs:max-w-[170px] sm:max-w-[200px] object-contain transition-all duration-500 group-hover:scale-105"
                style={{ maxHeight: '64px' }}
              />
            </Link>

            {/* Spacer left */}
            <div className="flex-1" />

            {/* ── Desktop nav ── */}
            <nav className="hidden lg:flex items-center gap-10">
              {navLinks.map(({ label, path }) => {
                const active = location.pathname === path
                return (
                  <Link
                    key={path}
                    to={path}
                    className="relative px-4 py-2 text-[16px] font-medium tracking-[-0.01em] transition-colors duration-200 select-none group"
                    style={{ color: active ? navActive : navTxt }}
                  >
                    <span className="relative z-10 transition-colors duration-200 group-hover:text-[#1a7a8a]">
                      {label}
                    </span>
                    {active && (
                      <motion.div
                        layoutId="nav-underline"
                        className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-full"
                        style={{ background: ulGrad }}
                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      />
                    )}
                  </Link>
                )
              })}

              {/* Products dropdown */}
              <div className="relative" ref={productsRef}>
                <button
                  onMouseEnter={() => { cancelClose(); setShowProductsDropdown(true) }}
                  onMouseLeave={scheduleClose}
                  className="relative flex items-center gap-1 px-4 py-2 text-[16px] font-medium tracking-[-0.01em] transition-colors duration-200 select-none group"
                  style={{ color: isProductsActive ? navActive : navTxt }}
                >
                  <span className="relative z-10 transition-colors duration-200 group-hover:text-[#1a7a8a]">
                    Products
                  </span>
                  <ChevronDown
                    className="relative z-10 w-3.5 h-3.5 transition-all duration-300 group-hover:text-[#1a7a8a]"
                    style={{ transform: showProductsDropdown ? "rotate(180deg)" : "rotate(0)" }}
                  />
                  {isProductsActive && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-full"
                      style={{ background: ulGrad }}
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </button>

                <AnimatePresence>
                  {showProductsDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.97 }}
                      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                      onMouseEnter={cancelClose}
                      onMouseLeave={scheduleClose}
                      className="absolute top-full left-[calc(50%-10px)] -translate-x-1/2 z-50"
                      style={{ paddingTop: "12px", width: "300px" }}
                    >
                      <div
                        style={{
                          borderRadius: "20px",
                          overflow:     "hidden",
                          background:   "white",
                          border:       "1px solid rgba(44,157,174,0.12)",
                          boxShadow:    "0 20px 60px rgba(26,122,138,0.14), 0 4px 16px rgba(44,157,174,0.08)",
                        }}
                      >
                        {/* Teal top accent bar */}
                        <div className="h-1" style={{ background: "linear-gradient(90deg, #1a7a8a, #2c9dae, #3bbac9)" }} />
                        <div className="p-2.5">
                          <p className="px-3 pt-1 pb-2 text-[10.5px] font-bold uppercase tracking-[0.16em]" style={{ color: "#2c9dae" }}>
                            Categories
                          </p>
                          {medicineCategories.map((cat) => (
                            <Link
                              key={cat.label}
                              to={cat.path}
                              onClick={() => setShowProductsDropdown(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-[14px] text-[15px] font-medium transition-all duration-200 group/item"
                              style={{ color: "#286074" }}
                              onMouseEnter={e => {
                                const el = e.currentTarget as HTMLElement
                                el.style.background = "rgba(44,157,174,0.08)"
                                el.style.color = "#145369"
                                el.style.transform = "translateX(4px)"
                              }}
                              onMouseLeave={e => {
                                const el = e.currentTarget as HTMLElement
                                el.style.background = "transparent"
                                el.style.color = "#286074"
                                el.style.transform = "translateX(0)"
                              }}
                            >
                              <span className="text-sm shrink-0 w-5 text-center">{cat.icon}</span>
                              <span>{cat.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* Spacer right */}
            <div className="flex-1" />

            {/* Right Side Actions Group (Cart + Mobile menu toggle) */}
            <div className="flex items-center gap-2 xs:gap-3 shrink-0">
              <Link
                to="/cart"
                className="relative flex items-center justify-center w-10 h-10 xs:w-11 xs:h-11 rounded-full transition-all duration-300"
                style={{
                  background: "rgba(44,157,174,0.08)",
                  color:      "#1a7a8a",
                  border:     "1.5px solid rgba(44,157,174,0.30)",
                }}
                aria-label={`Enquiry Cart with ${cartCount} items`}
              >
                <ShoppingCart className="w-[18px] h-[18px]" />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full px-1"
                    style={{
                      background: "linear-gradient(135deg, #f59e0b, #f97316)",
                      color: "white",
                      boxShadow: "0 2px 8px rgba(245,158,11,0.4)",
                    }}
                  >
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 active:scale-95"
                style={{
                  background: isMobileMenuOpen ? "rgba(44,157,174,0.20)" : "rgba(44,157,174,0.08)",
                  color:  "#1a7a8a",
                  border: "1.5px solid rgba(44,157,174,0.30)",
                }}
                aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={isMobileMenuOpen ? "close" : "menu"}
                    initial={{ rotate: -90, opacity: 0, scale: 0.8 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 90, opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                  >
                    {isMobileMenuOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
                  </motion.div>
                </AnimatePresence>
              </button>
            </div>

          </div>
        </div>
      </div>
    </header>

      {/* ── Mobile drawer ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-40 lg:hidden"
              style={{ background: "rgba(15,40,55,0.50)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
              className="lg:hidden fixed top-16 sm:top-20 left-2 xs:left-4 right-2 xs:right-4 z-50 rounded-2xl xs:rounded-3xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.15)]"
              style={{
                background: "white",
                maxHeight: "min(calc(100dvh - 75px), 640px)",
              }}
            >
              {/* Top accent bar */}
              <div className="h-1.5" style={{ background: "linear-gradient(90deg, #1a7a8a, #2c9dae, #3bbac9)" }} />

              <div className="px-4 xs:px-5 py-4 xs:py-5 overflow-y-auto" style={{ maxHeight: "calc(min(calc(100dvh - 75px), 640px) - 6px)" }}>
                {/* Nav Links */}
                <div className="space-y-1">
                  {navLinks.map(({ label, path }, i) => {
                    const active = location.pathname === path
                    return (
                      <motion.div
                        key={path}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.04, duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                      >
                        <Link
                          to={path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] font-medium transition-all duration-200 active:scale-[0.98]"
                          style={{
                            background: active ? "rgba(44,157,174,0.08)" : "transparent",
                            color:      active ? "#1a7a8a" : "#286074",
                          }}
                        >
                          <span
                            className="flex items-center justify-center w-9 h-9 rounded-full shrink-0"
                            style={{
                              background: active ? "rgba(44,157,174,0.12)" : "rgba(44,157,174,0.06)",
                            }}
                          >
                            {label === 'Home' ? '🏠' : label === 'About' ? '🏢' : '📞'}
                          </span>
                          <span className="flex-1">{label}</span>
                          {active && (
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#2c9dae" }} />
                          )}
                        </Link>
                      </motion.div>
                    )
                  })}
                </div>

                {/* Products Accordion */}
                <motion.div
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                  className="mt-1"
                >
                  <button
                    onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] font-medium transition-all duration-200 active:scale-[0.98]"
                    style={{
                      color: isProductsActive ? "#1a7a8a" : "#286074",
                      background: isProductsActive ? "rgba(44,157,174,0.08)" : "transparent",
                    }}
                  >
                    <span
                      className="flex items-center justify-center w-9 h-9 rounded-full shrink-0"
                      style={{
                        background: isProductsActive ? "rgba(44,157,174,0.12)" : "rgba(44,157,174,0.06)",
                      }}
                    >
                      💊
                    </span>
                    <span className="flex-1 text-left">Products</span>
                    <motion.div
                      animate={{ rotate: isMobileProductsOpen ? 180 : 0 }}
                      transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                    >
                      <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "#2c9dae" }} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isMobileProductsOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 mt-1 space-y-0.5 pb-1">
                          {medicineCategories.map((cat, ci) => (
                            <motion.div
                              key={cat.label}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: ci * 0.03, duration: 0.25 }}
                            >
                              <Link
                                to={cat.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 rounded-full text-[14px] font-medium transition-all duration-200 active:scale-[0.98]"
                                style={{ color: "#286074" }}
                              >
                                <span className="text-sm">{cat.icon}</span>
                                {cat.label}
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Divider */}
                <div className="mx-3 my-3 h-px" style={{ background: "rgba(44,157,174,0.10)" }} />

                {/* Cart Link */}
                <motion.div
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.24, duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                >
                  <Link
                    to="/cart"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] font-medium transition-all duration-200 active:scale-[0.98]"
                    style={{ color: "#286074" }}
                  >
                    <span
                      className="flex items-center justify-center w-9 h-9 rounded-full shrink-0"
                      style={{ background: "rgba(44,157,174,0.06)" }}
                    >
                      <ShoppingCart className="w-4 h-4" style={{ color: "#1a7a8a" }} />
                    </span>
                    <span className="flex-1">Enquiry Cart</span>
                    {cartCount > 0 && (
                      <span
                        className="text-[11px] font-bold rounded-full px-2.5 py-1 shrink-0"
                        style={{
                          background: "linear-gradient(135deg, #f59e0b, #f97316)",
                          color: "white",
                        }}
                      >
                        {cartCount}
                      </span>
                    )}
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}